import { useState, useRef, useEffect } from 'react';
import { func, shape, string, bool } from 'prop-types';
import { cloneDeep, debounce, noop, mapValues, keyBy } from 'lodash';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { ArrowForward as ArrowIcon } from '@material-ui/icons';

import EmptyState from '../../../../EmptyState';
import { getIsSustainabilityApp } from '../../utils';

import { RemoveAppButton, SettingsAccordion } from './components';
import { useStyles } from './styles';

import { withAssetEditorLockHOC } from '~/hocs';
import ErrorBoundary, { DevCenterAppErrorView } from '~/components/ErrorBoundary';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { ASSET_TYPES, DEV_CENTER_CLI_APP_ID } from '~/constants';
import AssetEditor from '~/components/AssetEditorV2';
import AppVersionsSelect from '~/components/AppVersionsSelect';
import { DisabledSettingsMessage } from '~/components/DisabledSettingsMessage';
import ConfirmationDialog from '~/components/ConfirmationDialog';
import { SEGMENT_TO_ASSET_TYPE, SEGMENTS } from '~/constants/segment';
import { showSuccessNotification } from '~/utils';
import AppIcon from '~/components/AppIcon';
import { getAppSegment } from '~/utils/devcenter';
import { PERMISSIONS, usePermissions } from '~/permissions';
import { isInsideNewMobileApp } from '~/utils/mobileDetect';

export const PAGE_NAME = 'DC_appSettings';

const getAppPageUrl = appId => `/app-store/app/${appId}`;
const AssetEditorWithLock = withAssetEditorLockHOC(AssetEditor);

function AppSettingsDialog({
  AppSettingsComponent,
  app,
  appData,
  appName,
  currentUser,
  isMultiRig,
  isSettingsDialogHidden,
  layoutEnvironment,
  onAppRemove,
  onCloneDashboard,
  onSettingsChange,
  openIntercom,
  rigId,
  toggleAppSettingsDialog,
}) {
  const hasSegmentSelector = app?.segment?.length > 1;
  const [appSegmentSetting, setAppSegmentSetting] = useState(getAppSegment(app));
  const isCompletionSegment = appSegmentSetting === SEGMENTS.COMPLETION;
  const assetEditorAppType = SEGMENT_TO_ASSET_TYPE[appSegmentSetting];
  const isAssetPage = layoutEnvironment && layoutEnvironment.type === 'asset';
  const [{ active: canReadAppPackages, loading: isPermissionsLoading }] = usePermissions([
    PERMISSIONS.getCanReadAppPackages(app.app.id),
  ]);

  const [isMounted, setIsMounted] = useState(false);
  const [isAssetSelectorRendered, setIsAssetSelectorRendered] = useState(true);
  const isAssetSelectorVisible =
    !isInsideNewMobileApp && !isMultiRig && !(isAssetPage && isCompletionSegment);
  const isRemoveButtonVisible = !isInsideNewMobileApp && !isSettingsDialogHidden && onAppRemove;

  // NOTE: Keep in ref to have a way to reset default value
  // of asset selector when changing app segment
  const defaultRigId = useRef(rigId);

  useEffect(() => {
    if (!isAssetSelectorRendered) setIsAssetSelectorRendered(true);
  }, [isAssetSelectorRendered]);

  const [appSettings, setAppSettings] = useState(() => ({
    ...cloneDeep(app.settings),
    package: app.settings.package || app?.package?.version,
  }));

  const handleChangeAppSegment = ({ target: { value: nextSegment } }) => {
    defaultRigId.current = null;
    setIsAssetSelectorRendered(false);

    setAppSegmentSetting(nextSegment);
    setAppSettings({
      package: appSettings.package,
      segment: nextSegment,
      // NOTE: we need to reset asset ids when segment is changed
      ...mapValues(keyBy(ASSET_TYPES, 'id'), () => null),
      assets: null,
    });
  };

  const isSustainabilityApp = getIsSustainabilityApp(app?.app?.id);

  const [validationError, setValidationError] = useState('');
  const platformAppSettingsRef = useRef(null);
  const customAppSettingsRef = useRef(null);

  const hasPlatformAppSettings =
    isPermissionsLoading ||
    canReadAppPackages ||
    Boolean(platformAppSettingsRef.current?.clientHeight);
  const hasCustomAppSettings = Boolean(customAppSettingsRef.current?.clientHeight);
  const isAccordionRequired = hasPlatformAppSettings && hasCustomAppSettings;

  const styles = useStyles({
    isCompletionApp: isCompletionSegment,
    isSustainabilityApp,
    isAccordionRequired,
    isRemoveButtonVisible,
  });

  const isCLIApp = app.id === DEV_CENTER_CLI_APP_ID;

  const onSave = () => {
    const isNoPackageError = !appSettings.package && !isCLIApp;
    if (isNoPackageError) return setValidationError('Please select app version');
    if (isAssetSelectorVisible && !isAssetPage) {
      const appTypeKey = assetEditorAppType?.primaryAsset.assetType.id;
      const appTypeLabel = assetEditorAppType?.primaryAsset.assetType.label;
      if (appTypeKey && !appSettings[appTypeKey])
        return setValidationError(`Please select ${appTypeLabel}`);
    }

    onSettingsChange(appSettings);
    showSuccessNotification('Successfully saved');
    return toggleAppSettingsDialog();
  };

  const onAppSettingChange = (name, value) =>
    setAppSettings(prevSettings => ({
      ...prevSettings,
      [name]: value,
    }));

  const onAppSettingsChange = newSettings =>
    setAppSettings(prevSettings => ({ ...prevSettings, ...newSettings }));

  const AssetEditorComponent = isAssetPage ? AssetEditorWithLock : AssetEditor;

  useEffect(() => {
    const debouncedSetIsMounted = debounce(setIsMounted, 200);
    debouncedSetIsMounted(true);
  }, []);

  const modalTitle = (
    <div className={styles.modalTitleContainer}>
      <AppIcon
        className={styles.appIcon}
        segment={[appSegmentSetting]}
        iconUrl={app?.app?.icon?.url}
        height={48}
        width={48}
      />
      <div>
        <div>{`${appName} Settings`}</div>
        {!isInsideNewMobileApp && (
          <div
            data-testid={`${PAGE_NAME}_appstoreLink`}
            className={styles.appStoreButton}
            onClick={() => window.open(getAppPageUrl(app?.app?.id), '_top')}
          >
            Open in Appstore <ArrowIcon className={styles.arrowIcon} />
          </div>
        )}
      </div>
    </div>
  );

  const modalActions = (
    <div className={styles.modalActions}>
      {isRemoveButtonVisible && <RemoveAppButton appName={appName} onAppRemove={onAppRemove} />}
      <div>
        <Button
          data-testid={`${PAGE_NAME}_cancel`}
          variation="secondary"
          onClick={toggleAppSettingsDialog}
        >
          Cancel
        </Button>
        {isSettingsDialogHidden && (
          <Button variation="primary" onClick={() => onCloneDashboard()}>
            Copy Dashboard
          </Button>
        )}
        {!isSettingsDialogHidden && (
          <Button
            data-testid={`${PAGE_NAME}_save`}
            className={styles.saveButton}
            variation="primary"
            onClick={onSave}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      actions={modalActions}
      closeIconClassName={styles.closeIcon}
      contentContainerClassName={styles.modalContainer}
      modalTitleClassName={styles.modalTitle}
      onClose={toggleAppSettingsDialog}
      open
      title={modalTitle}
    >
      <ErrorBoundary ErrorView={DevCenterAppErrorView} openIntercom={openIntercom}>
        <ConfirmationDialog
          open={!!validationError}
          isShowCancel={false}
          title="Validation failed"
          text={validationError}
          handleOk={() => setValidationError('')}
          classes={{ confirmation: styles.confirmationText }}
        />
        {isSettingsDialogHidden && <DisabledSettingsMessage />}
        {!isSettingsDialogHidden &&
          isMounted &&
          !hasCustomAppSettings &&
          !hasPlatformAppSettings && (
            <EmptyState image={EmptyState.IMAGES.UnderConstruction} title="No app settings" />
          )}
        {!isSettingsDialogHidden && (
          <>
            <SettingsAccordion
              defaultExpanded
              className={styles.assetAccordion}
              accordionDetailsRef={platformAppSettingsRef}
              isAccordionRequired={isAccordionRequired}
              summary="Asset"
            >
              {hasSegmentSelector && (
                <FormControl className={styles.segmentSelector}>
                  <InputLabel>Segment</InputLabel>
                  <Select value={appSegmentSetting} onChange={handleChangeAppSegment}>
                    {app.segment.map(segmentItem => (
                      <MenuItem
                        className={styles.segmentMenuItem}
                        key={segmentItem}
                        value={segmentItem}
                      >
                        {segmentItem}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {isAssetSelectorVisible && isAssetSelectorRendered && (
                <AssetEditorComponent
                  appKey={isCLIApp ? null : app.app.app_key}
                  appType={assetEditorAppType}
                  defaultValue={defaultRigId.current}
                  secondaryDefaultValue={+appData?.well?.id}
                  isHiddenMultipleAssetsToggle
                  isHiddenTitle
                  isNullable={isAssetPage}
                  label="Asset"
                  onSettingsChange={onAppSettingsChange}
                  settings={appSettings}
                />
              )}
              <AppVersionsSelect
                className={styles.appVersionSelect}
                appId={app.app.id}
                value={appSettings.package}
                onChange={val => onAppSettingChange('package', val)}
              />
            </SettingsAccordion>
            <SettingsAccordion
              accordionDetailsRef={customAppSettingsRef}
              className={styles.accordion}
              defaultExpanded
              isAccordionRequired={isAccordionRequired}
              summary="App Settings"
            >
              {AppSettingsComponent && (
                <AppSettingsComponent
                  app={app}
                  appData={appData}
                  settings={appSettings}
                  onSettingChange={onAppSettingChange}
                  onSettingsChange={onAppSettingsChange}
                  layoutEnvironment={layoutEnvironment}
                  currentUser={currentUser}
                />
              )}
            </SettingsAccordion>
          </>
        )}
      </ErrorBoundary>
    </Modal>
  );
}

AppSettingsDialog.propTypes = {
  AppSettingsComponent: func,
  app: shape({}).isRequired,
  appData: shape({}).isRequired,
  appName: string.isRequired,
  currentUser: shape({}).isRequired,
  isMultiRig: bool.isRequired,
  isSettingsDialogHidden: bool,
  layoutEnvironment: shape({}).isRequired,
  onAppRemove: func,
  onCloneDashboard: func,
  onSettingsChange: func.isRequired,
  toggleAppSettingsDialog: func.isRequired,
};

AppSettingsDialog.defaultProps = {
  AppSettingsComponent: () => null,
  isSettingsDialogHidden: false,
  onAppRemove: undefined,
  onCloneDashboard: noop,
};

export default AppSettingsDialog;
